import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Redirect } from "react-router-dom";
import axios from "axios";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import { Modal } from "react-bootstrap";
import MdlAddOutsideParticipant from "../dialog/MdlAddOutsideParticipant";
import MdlEditEventParticipant from "../dialog/MdlEditEventParticipant";
import MdlEventParticipantRegistration from "../dialog/MdlEventParticipantRegistration";
import "../css/Admin_css/admin-customize.css";

// Helper function to build API URL
const buildApiUrl = (functionName) => {
  return `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}${functionName}`;
};

// Default payment form data
const getDefaultPaymentFormData = () => ({
  payable_amount: "",
  discount_amount: "0",
  payment_amount: "",
  payment_date: new Date().toISOString().split('T')[0],
  payment_mode: "Cash",
  transaction_id: "",
  receipt_no: "",
  remark: "",
});

// Default form data
const getDefaultFormData = () => ({
  student_name: "",
  student_dob: "",
  gender: "",
  community: "",
  religion: "",
  nationality: "",
  current_school_name: "",
  board_id: "",
  medium_id: "",
  reading_class: "",
  reading_class_id: "",
  student_aadhar: "",
  mother_name: "",
  father_name: "",
  contact_number: "",
  whatsapp_number: "",
  address: "",
  remark: "",
  group_id: "",
});

const ManageEventParticipant = () => {
  const { event_id, session_id } = useParams();
  const [loggedIn, setLoggedIn] = useState(localStorage.getItem("token") !== null);
  const [loading, setLoading] = useState(false);
  const [loadingEventDetails, setLoadingEventDetails] = useState(false);
  const [loadingParticipantGroups, setLoadingParticipantGroups] = useState(false);
  const [loadingEventPrograms, setLoadingEventPrograms] = useState(false);
  const [loadingMessagePoints, setLoadingMessagePoints] = useState(false);
  const [participantList, setParticipantList] = useState([]);
  const [eventDetails, setEventDetails] = useState(null);
  const [participantGroups, setParticipantGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddOutsideStudentModal, setShowAddOutsideStudentModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [eventPrograms, setEventPrograms] = useState([]);
  const [paymentFormData, setPaymentFormData] = useState(getDefaultPaymentFormData());
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [selectedStudentsForRegistration, setSelectedStudentsForRegistration] = useState([]);
  const [selectedGroupForAdd, setSelectedGroupForAdd] = useState(null);
  const [editingParticipant, setEditingParticipant] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [formData, setFormData] = useState(getDefaultFormData());
  const [messagePoints, setMessagePoints] = useState([]);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoggedIn(false);
      return;
    }
    
    if (event_id && session_id) {
      getEventDetails();
      getParticipantGroups();
      getParticipantList();
      getEventPrograms();
      getMessagePoints();
    }
  }, [event_id, session_id]);

  const getEventDetails = useCallback(() => {
    setLoadingEventDetails(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("getEventDetails.php");

    axios
      .get(URL, { params: { event_id, school_code: SCHOOL_CODE } })
      .then((res) => res.data)
      .then((response) => {
        setLoadingEventDetails(false);
        if (response.api_status === "OK" && response.data?.length > 0) {
          setEventDetails(response.data[0]);
        }
      })
      .catch((error) => {
        setLoadingEventDetails(false);
        console.error("Error fetching event details:", error);
      });
  }, [event_id]);

  const getParticipantGroups = useCallback(() => {
    setLoadingParticipantGroups(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("getEventParticipantGroups.php");

    axios
      .get(URL, { params: { event_id, session_id, school_code: SCHOOL_CODE } })
      .then((res) => res.data)
      .then((response) => {
        setLoadingParticipantGroups(false);
        if (response.api_status === "OK") {
          setParticipantGroups(response.data || []);
        }
      })
      .catch((error) => {
        setLoadingParticipantGroups(false);
        console.error("Error fetching participant groups:", error);
      });
  }, [event_id, session_id]);

  const getParticipantList = useCallback(() => {
    setLoading(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("getAllEventParticipants.php");
    
    const fullUrl = `${URL}?event_id=${event_id}&session_id=${session_id}&school_code=${SCHOOL_CODE}`;
    console.log("getParticipantList URL:", fullUrl);

    axios
      .get(URL, { params: { event_id, session_id, school_code: SCHOOL_CODE } })
      .then((res) => res.data)
      .then((response) => {
        setLoading(false);
        if (response.api_status === "OK") {
          setParticipantList(response.data || []);
        } else {
          myToast.notify_warning(response.api_remark || "Failed to load participants");
          setParticipantList([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching participants:", error);
        myToast.notify_error("Error loading participants");
        setParticipantList([]);
      });
  }, [event_id, session_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = useCallback(() => {
    setFormData(getDefaultFormData());
    setEditingParticipant(null);
  }, []);

  const handleOpenAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    resetForm();
  };

  const handleOpenAddSchoolStudentModal = useCallback(() => {
    resetForm();
    
    if (participantGroups.length === 0) {
      myToast.notify_warning("No participant groups available. Please create groups first.");
      return;
    }
    
    const studentGroup = participantGroups.find(g => g.group_name === "STUDENT");
    const defaultGroup = studentGroup || participantGroups[0];
    setSelectedGroupForAdd(defaultGroup);
    setSelectedStudentsForRegistration([]);
    setShowRegistrationModal(true);
  }, [participantGroups, resetForm]);


  const handleCloseRegistrationModal = useCallback(() => {
    setShowRegistrationModal(false);
    setSelectedStudentsForRegistration([]);
    setSelectedGroupForAdd(null);
  }, []);

  const handleSelectedStudents = useCallback((selectedStudents) => {
    setSelectedStudentsForRegistration(selectedStudents);
  }, []);

  const handleOpenAddOutsideStudentModal = useCallback(() => {
    // Open the printable registration form in a modal within the app
    setShowAddOutsideStudentModal(true);
  }, []);

  const handleCloseAddOutsideStudentModal = useCallback(() => {
    setShowAddOutsideStudentModal(false);
    resetForm();
  }, [resetForm]);

  const handleOpenEditModal = useCallback((participant) => {
    if (!participant) {
      myToast.notify_warning("Participant data not available");
      return;
    }
    setEditingParticipant(participant);
    setTimeout(() => setShowEditModal(true), 0);
  }, []);

  const handleOpenDetailModal = async (participant) => {
    setSelectedParticipant(participant);
    setShowDetailModal(true);
    // Fetch event programs
    await getEventPrograms();
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedParticipant(null);
  };

  const handleNextParticipant = useCallback(() => {
    if (!selectedParticipant || !participantList || participantList.length === 0) return;
    
    const currentIndex = participantList.findIndex(
      p => p.paricipant_id === selectedParticipant.paricipant_id
    );
    
    if (currentIndex === -1) return;
    
    const nextIndex = (currentIndex + 1) % participantList.length;
    setSelectedParticipant(participantList[nextIndex]);
  }, [selectedParticipant, participantList]);

  const handlePreviousParticipant = useCallback(() => {
    if (!selectedParticipant || !participantList || participantList.length === 0) return;
    
    const currentIndex = participantList.findIndex(
      p => p.paricipant_id === selectedParticipant.paricipant_id
    );
    
    if (currentIndex === -1) return;
    
    const prevIndex = currentIndex === 0 ? participantList.length - 1 : currentIndex - 1;
    setSelectedParticipant(participantList[prevIndex]);
  }, [selectedParticipant, participantList]);

  const getEventPrograms = useCallback(async () => {
    setLoadingEventPrograms(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("getEventPrograms.php");

    try {
      const response = await axios.get(URL, {
        params: { event_id, session_id, school_code: SCHOOL_CODE },
      });

      setLoadingEventPrograms(false);
      if (response.data.api_status === "OK" && Array.isArray(response.data.data)) {
        setEventPrograms(response.data.data || []);
      }
    } catch (error) {
      setLoadingEventPrograms(false);
      console.error("Error fetching event programs:", error);
    }
  }, [event_id, session_id]);

  const getMessagePoints = useCallback(async () => {
    setLoadingMessagePoints(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("getEventMessagePoints.php");

    try {
      const response = await axios.get(URL, {
        params: { 
          event_id, 
          session_id, 
          school_code: SCHOOL_CODE,
          status: 'Active'
        },
      });

      setLoadingMessagePoints(false);
      if (response.data.api_status === "OK" && Array.isArray(response.data.data)) {
        setMessagePoints(response.data.data || []);
      }
    } catch (error) {
      setLoadingMessagePoints(false);
      console.error("Error fetching message points:", error);
    }
  }, [event_id, session_id]);

  const handleOpenPaymentModal = useCallback((participant) => {
    setSelectedParticipant(participant);
    const payableAmount = parseFloat(participant.payable_amount) || 0;
    const discountAmount = parseFloat(participant.discount_amount) || 0;
    const defaultPaymentAmount = Math.max(0, payableAmount - discountAmount);
    
    setPaymentFormData({
      ...getDefaultPaymentFormData(),
      payable_amount: participant.payable_amount || "",
      discount_amount: participant.discount_amount || "0",
      payment_amount: defaultPaymentAmount > 0 ? defaultPaymentAmount.toString() : "",
    });
    setShowPaymentModal(true);
  }, []);

  const handleClosePaymentModal = useCallback(() => {
    setShowPaymentModal(false);
    setPaymentFormData(getDefaultPaymentFormData());
  }, []);

  const handlePaymentInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setPaymentFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const calculateDueAmount = useMemo(() => {
    const payable = parseFloat(paymentFormData.payable_amount) || 0;
    const discount = parseFloat(paymentFormData.discount_amount) || 0;
    const paid = parseFloat(paymentFormData.payment_amount) || 0;
    return Math.max(0, payable - discount - paid).toFixed(2);
  }, [paymentFormData.payable_amount, paymentFormData.discount_amount, paymentFormData.payment_amount]);

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    
    if (!paymentFormData.payment_amount || parseFloat(paymentFormData.payment_amount) <= 0) {
      myToast.notify_warning("Please enter a valid payment amount");
      return;
    }
    
    if (!paymentFormData.payment_date) {
      myToast.notify_warning("Please select payment date");
      return;
    }

    if (!selectedParticipant) {
      myToast.notify_error("Participant information not found");
      return;
    }

    setSavingPayment(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const STAFF_ID = SessionManager.getStaffDetails()?.staff_id || "";
    const URL = buildApiUrl("AddEventParticipantPayment.php");
    
    const params = {
      participant_id: selectedParticipant.paricipant_id,
      event_id: event_id,
      session_id: session_id,
      school_code: SCHOOL_CODE,
      payable_amount: parseFloat(paymentFormData.payable_amount) || null,
      discount_amount: parseFloat(paymentFormData.discount_amount) || 0,
      payment_amount: parseFloat(paymentFormData.payment_amount),
      payment_date: paymentFormData.payment_date,
      payment_mode: paymentFormData.payment_mode,
      transaction_id: paymentFormData.transaction_id || "",
      receipt_no: paymentFormData.receipt_no || "",
      staff_id: STAFF_ID || "",
      remark: paymentFormData.remark || "",
    };
    
    console.log("AddEventParticipantPayment URL:", URL);
    console.log("AddEventParticipantPayment Params:", params);
    console.log("AddEventParticipantPayment Full URL:", `${URL}?${new URLSearchParams(params).toString()}`);

    try {
      const response = await axios.get(URL, { params });

      setSavingPayment(false);
      if (response.data.api_status === "OK") {
        myToast.notify_success("Payment recorded successfully");
        handleClosePaymentModal();
        getParticipantList();
        // Update the selected participant
        if (selectedParticipant) {
          setSelectedParticipant({
            ...selectedParticipant,
            is_event_fee_paid: 1,
          });
        }
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to record payment");
      }
    } catch (error) {
      setSavingPayment(false);
      console.error("Error recording payment:", error);
      myToast.notify_error("Error recording payment: " + (error.response?.data?.api_remark || error.message));
    }
  };

  const handleUpdatePaymentStatus = useCallback(async (participantId, paymentStatus) => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("UpdateEventParticipantPayment.php");

    try {
      const response = await axios.get(URL, {
        params: {
          participant_id: participantId,
          school_code: SCHOOL_CODE,
          payment_status: paymentStatus ? 1 : 0,
          payment_mode: "Cash",
          payment_remark: "Updated by admin",
        },
      });

      if (response.data.api_status === "OK") {
        myToast.notify_success("Payment status updated successfully");
        getParticipantList();
        // Update the selected participant
        if (selectedParticipant && selectedParticipant.paricipant_id === participantId) {
          setSelectedParticipant({
            ...selectedParticipant,
            is_event_fee_paid: paymentStatus ? 1 : 0,
          });
        }
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to update payment status");
      }
    } catch (error) {
      console.error("Error updating payment status:", error);
      myToast.notify_error("Error updating payment status");
    }
  }, [selectedParticipant]);

  const handleUpdateProgram = useCallback(async (participantId, programId) => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("UpdateEventParticipantProgram.php");

    try {
      const response = await axios.get(URL, {
        params: {
          participant_id: participantId,
          program_id: programId,
          school_code: SCHOOL_CODE,
        },
      });

      if (response.data.api_status === "OK") {
        myToast.notify_success("Program updated successfully");
        getParticipantList();
        // Update the selected participant
        if (selectedParticipant && selectedParticipant.paricipant_id === participantId) {
          setSelectedParticipant({
            ...selectedParticipant,
            program_id: programId,
          });
        }
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to update program");
      }
    } catch (error) {
      console.error("Error updating program:", error);
      myToast.notify_error("Error updating program");
    }
  }, [selectedParticipant]);

  const handleDeleteParticipant = useCallback(async (participantId) => {
    if (!window.confirm("Are you sure you want to delete this participant? This action cannot be undone.")) {
      return;
    }

    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const STAFF_ID = SessionManager.getStaffDetails()?.staff_id || "";
    const URL = buildApiUrl("DeleteEventParticipant.php");

    try {
      const response = await axios.get(URL, {
        params: {
          participant_id: participantId,
          school_code: SCHOOL_CODE,
          session_id: session_id,
          staff_id: STAFF_ID,
        },
      });

      if (response.data.api_status === "OK") {
        myToast.notify_success("Participant deleted successfully");
        handleCloseDetailModal();
        getParticipantList();
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to delete participant");
      }
    } catch (error) {
      console.error("Error deleting participant:", error);
      myToast.notify_error("Error deleting participant");
    }
  }, [session_id]);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditingParticipant(null);
  }, []);

  const buildWhatsAppMessage = useCallback(() => {
    // Build message from template
    let message = `प्रिय विद्यार्थियों,\n\n`;
    const eventName = eventDetails?.event_name || '';
    if (eventName) {
      message += `${eventName} में आपका पंजीकरण सफलतापूर्वक हो चुका है।\n`;
    } else {
      message += `आपका पंजीकरण सफलतापूर्वक हो चुका है।\n`;
    }
    message += `कृपया परीक्षा के लिए नीचे दिए गए निर्देशों का पालन अवश्य करें:\n\n`;

    // Add message points from database
    if (messagePoints && messagePoints.length > 0) {
      messagePoints.forEach((point, index) => {
        if (point.message_point && point.status === 'Active') {
          message += `${index + 1}. ${point.message_point}\n\n`;
        }
      });
    }

    message += `Result Information\n\n`;
    
    // Add result date from eventDetails
    const resultDate = eventDetails?.result_date || '';
    message += `रिज़ल्ट ${resultDate} को हमारी वेबसाइट के "Result" सेक्शन में उपलब्ध होगा।\n`;
    message += `The result will be declared on ${resultDate} on our website in the "Result" section.\n\n`;
    
    message += `Contact Information\n\n`;
    
    // Add official number from eventDetails
    const officialNumber = eventDetails?.official_number || eventDetails?.['official number'] || '';
    message += `अधिक जानकारी के लिए हमारे ऑफ़िशियल नंबर — ${officialNumber} पर संपर्क करें।\n`;
    message += `For more information, please contact us on our official number — ${officialNumber}.\n`;
    
    // Add Google Map from eventDetails
    const googleMap = eventDetails?.google_map || '';
    if (googleMap) {
      message += `Google map:- ${googleMap}\n`;
    }
    
    // Add address from event_venue
    const eventVenue = eventDetails?.event_venue || '';
    if (eventVenue) {
      message += `Address:- ${eventVenue}\n\n`;
    }
    
    // Add footer from remark
    const eventRemark = eventDetails?.remark || '';
    if (eventRemark) {
      message += `${eventRemark}\n`;
    }
    
    return message;
  }, [eventDetails, messagePoints]);

  const handleSendWhatsAppMessage = useCallback(() => {
    if (!selectedParticipant) return;

    const phoneNumber = selectedParticipant.whatsapp_number || selectedParticipant.contact_number;
    if (!phoneNumber) {
      myToast.notify_warning("No WhatsApp or contact number available for this participant");
      return;
    }

    // Remove any non-numeric characters except + sign
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
    
    // Ensure number starts with country code (if it doesn't start with +, assume it's a local number)
    let whatsappNumber = cleanNumber;
    if (!cleanNumber.startsWith('+')) {
      // If number doesn't start with +, add country code (assuming India +91)
      // Remove leading 0 if present
      const numberWithoutZero = cleanNumber.startsWith('0') ? cleanNumber.substring(1) : cleanNumber;
      whatsappNumber = `91${numberWithoutZero}`;
    } else {
      // Remove + sign for WhatsApp URL
      whatsappNumber = cleanNumber.substring(1);
    }
    
    // Build message
    const message = buildWhatsAppMessage();
    
    // Create WhatsApp URL
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
    
    // Open WhatsApp in a new window
    window.open(whatsappUrl, '_blank');
  }, [selectedParticipant, buildWhatsAppMessage]);

  const handleMarkAsSent = useCallback(async (participantId) => {
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("UpdateEventParticipantMessageStatus.php");

    try {
      const response = await axios.get(URL, {
        params: {
          participant_id: participantId,
          school_code: SCHOOL_CODE,
          message_sent: 1,
        },
      });

      if (response.data.api_status === "OK") {
        myToast.notify_success("Marked as sent successfully");
        getParticipantList();
        // Update the selected participant
        if (selectedParticipant && selectedParticipant.paricipant_id === participantId) {
          setSelectedParticipant({
            ...selectedParticipant,
            message_send: 1,
          });
        }
      } else {
        myToast.notify_warning(response.data.api_remark || "Failed to mark as sent");
      }
    } catch (error) {
      console.error("Error marking as sent:", error);
      myToast.notify_error("Error marking as sent: " + (error.response?.data?.api_remark || error.message));
    }
  }, [selectedParticipant]);

  const handleWhatsAppMessage = useCallback((participant) => {
    // This function is kept for backward compatibility but now just calls handleSendWhatsAppMessage
    // The preview is shown inline in the detail modal
    handleSendWhatsAppMessage();
  }, [handleSendWhatsAppMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.student_name.trim() || !formData.father_name.trim() || !formData.contact_number.trim()) {
      myToast.notify_warning("Please fill in all required fields");
      return;
    }

    setSaving(true);
    const SCHOOL_CODE = SessionManager.getSchoolCode();
    const URL = buildApiUrl("AddEventParticipant.php");

    const params = {
      event_id: event_id,
      session_id: session_id,
      student_name: formData.student_name.trim(),
      student_dob: formData.student_dob || "",
      gender: formData.gender || "",
      community: formData.community || "",
      religion: formData.religion || "",
      nationality: formData.nationality || "",
      current_school_name: formData.current_school_name || "",
      board_id: formData.board_id || "",
      medium_id: formData.medium_id || "",
      reading_class: formData.reading_class || "",
      reading_class_id: formData.reading_class_id || "",
      student_aadhar: formData.student_aadhar || "",
      mother_name: formData.mother_name || "",
      father_name: formData.father_name.trim(),
      contact_number: formData.contact_number.trim(),
      whatsapp_number: formData.whatsapp_number || formData.contact_number.trim(),
      address: formData.address || "",
      remark: formData.remark || "",
      group_id: formData.group_id || "",
      school_code: SCHOOL_CODE,
    };

    axios
      .get(URL, { params })
      .then((res) => res.data)
      .then((response) => {
        setSaving(false);
        if (response.api_status === "OK") {
          myToast.notify_success("Participant added successfully");
          handleCloseAddModal();
          handleCloseAddOutsideStudentModal();
          getParticipantList();
        } else {
          myToast.notify_warning(response.api_remark || "Failed to add participant");
        }
      })
      .catch((error) => {
        setSaving(false);
        console.error("Error adding participant:", error);
        myToast.notify_error("Error adding participant");
      });
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  }, []);

  const filteredParticipants = useMemo(() => {
    return selectedGroupId === "all" 
      ? participantList 
      : participantList.filter(p => p.group_id === selectedGroupId);
  }, [selectedGroupId, participantList]);

  if (!loggedIn) {
    return <Redirect to="/" />;
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div className="content" style={{ background: "var(--grey0)", minHeight: "100vh" }}>
      <style>{`
        .participant-detail-content {
          border-radius: 12px;
          overflow: hidden;
          background: transparent;
          border: none;
        }
        .participant-detail-content .modal-content {
          border: none;
          border-radius: 12px;
          overflow: hidden;
          background: transparent;
          box-shadow: 0 18px 40px rgba(15,23,42,0.28);
        }
      `}</style>
      {/* Header Section */}
      <div className="content-header d-flex justify-content-between align-items-center">
        <div className="leftside-content-header d-flex align-items-center">
          <ul className="breadcrumbs mb-0">
            <li>
              <i className="fa fa-home" aria-hidden="true" />
              <a href="#">
                Manage Participants
                {participantList.length > 0 && ` (${participantList.length})`}
              </a>
            </li>
          </ul>
        </div>
        <div>
          <BackButton />
        </div>
      </div>

      

      {/* Content Card */}
      <div
        className="animated fadeInUp page-card box_shadow"
        style={{ padding: "10px", maxWidth: "100%" }}
      >
        {/* Event Header - Inside Content Area */}
        {eventDetails && (
          <div
            style={{
              padding: "16px 20px",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
              color: "#fff",
              borderRadius: "10px 10px 0 0",
              marginBottom: "20px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "10px",
                  background: "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                }}
              >
                <i className="fa fa-calendar-check" />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: "20px", fontWeight: 700 }}>
                  {eventDetails.event_name}
                </h3>
                <p style={{ margin: "4px 0 0 0", fontSize: "14px", opacity: 0.9 }}>
                  Event ID: {eventDetails.event_id}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Group Selection Tabs */}
        {participantGroups.length > 0 && (
          <div style={{ marginBottom: "20px", display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setSelectedGroupId("all")}
              style={{
                padding: "8px 20px",
                borderRadius: "8px",
                border: "none",
                background: selectedGroupId === "all" 
                  ? `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`
                  : COLORS.white,
                color: selectedGroupId === "all" ? "#fff" : COLORS.black8,
                fontWeight: 600,
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.3s ease",
                border: selectedGroupId === "all" ? "none" : `2px solid ${COLORS.grey3 || "#E0E0E0"}`,
                boxShadow: selectedGroupId === "all" ? "0 2px 8px rgba(59, 130, 246, 0.3)" : "none",
              }}
              onMouseEnter={(e) => {
                if (selectedGroupId !== "all") {
                  e.currentTarget.style.borderColor = COLORS.primary_color_shade6;
                  e.currentTarget.style.background = COLORS.primary_color_shade1;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedGroupId !== "all") {
                  e.currentTarget.style.borderColor = COLORS.grey3;
                  e.currentTarget.style.background = COLORS.white;
                }
              }}
            >
              All Participants ({participantList.length})
            </button>
            {participantGroups.map((group) => {
              const groupParticipantCount = participantList.filter(p => p.group_id === group.group_id).length;
              return (
                <button
                  key={group.group_id}
                  type="button"
                  onClick={() => setSelectedGroupId(group.group_id)}
                  style={{
                    padding: "8px 20px",
                    borderRadius: "8px",
                    border: "none",
                    background: selectedGroupId === group.group_id 
                      ? `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`
                      : COLORS.white,
                    color: selectedGroupId === group.group_id ? "#fff" : COLORS.black8,
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    border: selectedGroupId === group.group_id ? "none" : `2px solid ${COLORS.grey3 || "#E0E0E0"}`,
                    boxShadow: selectedGroupId === group.group_id ? "0 2px 8px rgba(59, 130, 246, 0.3)" : "none",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedGroupId !== group.group_id) {
                      e.currentTarget.style.borderColor = COLORS.primary_color_shade6;
                      e.currentTarget.style.background = COLORS.primary_color_shade1;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedGroupId !== group.group_id) {
                      e.currentTarget.style.borderColor = COLORS.grey3;
                      e.currentTarget.style.background = COLORS.white;
                    }
                  }}
                >
                  {group.group_name} ({groupParticipantCount})
                </button>
              );
            })}
          </div>
        )}

        {/* Filtered Participant List */}
        {filteredParticipants.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#6b7280",
            }}
          >
            <i
              className="fa fa-users"
              style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}
            />
            <p style={{ fontSize: "16px", margin: 0 }}>
              {selectedGroupId === "all" 
                ? "No participants found. Click \"Add Participant\" to add one."
                : `No participants found in this group. Click "Add Participant" to add one.`}
            </p>
          </div>
        ) : (
            <div style={{ 
              overflowX: "auto", 
              background: "#fff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
              border: "1px solid #e5e7eb",
              width: "100%"
            }}>
              <table style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                fontSize: "14px",
                minWidth: "1100px"
              }}>
                <thead>
                  <tr style={{
                    background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                    color: "#fff"
                  }}>
                    {[
                      "#",
                      "Student Name",
                      "Father Name",
                      "Group",
                      "Class",
                      "Program",
                      "Contact",
                      "Address",
                      "Fee Paid",
                      "Message",
                      "Actions",
                    ].map((label) => (
                      <th
                        key={label}
                        style={{
                          padding: "14px 12px",
                          textAlign: label === "Actions" ? "center" : "left",
                          fontWeight: 700,
                          fontSize: "12px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          borderBottom: "none",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredParticipants.map((participant, index) => {
                    const participantGroup = participantGroups.find(g => g.group_id === participant.group_id);
                    return (
                      <tr 
                        key={participant.paricipant_id || index}
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                          transition: "all 0.2s ease",
                          background: index % 2 === 0 ? "#fff" : "#f9fafb"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "#f3f4f6";
                          e.currentTarget.style.transform = "scale(1.001)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = index % 2 === 0 ? "#fff" : "#f9fafb";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        <td style={{
                          padding: "12px",
                          color: "#6b7280",
                          fontWeight: 600,
                          fontSize: "13px"
                        }}>{index + 1}</td>
                        <td style={{
                          padding: "12px",
                          fontWeight: 600,
                          color: "#111827",
                          fontSize: "13px",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }} title={participant.student_name || ""}>{participant.student_name || "N/A"}</td>
                        <td style={{
                          padding: "12px",
                          color: "#374151",
                          fontSize: "12px",
                          maxWidth: "150px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }} title={participant.father_name || ""}>{participant.father_name || "N/A"}</td>
                        <td style={{ padding: "12px" }}>
                          {participantGroup ? (
                            <span style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                              color: "#fff"
                            }}>
                              {participantGroup.group_name}
                            </span>
                          ) : (
                            <span style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: "#e5e7eb",
                              color: "#6b7280"
                            }}>
                              No Group
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: "12px",
                          color: "#374151",
                          fontSize: "12px"
                        }}>{participant.reading_class || "N/A"}</td>
                        <td style={{
                          padding: "12px",
                          color: "#374151",
                          fontSize: "12px",
                          fontFamily: "monospace"
                        }}>
                          {eventPrograms.find(p => p.program_id === participant.program_id)?.program_name
                            || participant.program_name
                            || participant.program_id
                            || "N/A"}
                        </td>
                        <td style={{
                          padding: "12px",
                          color: "#374151",
                          fontSize: "12px",
                          fontFamily: "monospace"
                        }}>{participant.contact_number || "N/A"}</td>
                        <td style={{
                          padding: "12px",
                          color: "#374151",
                          fontSize: "12px",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap"
                        }} title={participant.address || ""}>{participant.address || "N/A"}</td>
                        <td style={{ padding: "12px" }}>
                          {participant.is_event_fee_paid == 1 ? (
                            <span style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                              color: "#fff"
                            }}>
                              Paid
                            </span>
                          ) : (
                            <span style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: "#fef3c7",
                              color: "#92400e"
                            }}>
                              Unpaid
                            </span>
                          )}
                        </td>
                        <td style={{
                          padding: "12px",
                          textAlign: "center",
                          whiteSpace: "nowrap"
                        }}>
                          {participant.message_send == 1 ? (
                            <span style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              padding: "4px 8px",
                              borderRadius: "4px",
                              fontSize: "11px",
                              fontWeight: 600,
                              background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                              color: "#fff"
                            }}>
                              <i className="fa fa-check-circle" style={{ fontSize: "10px" }} />
                              Sent
                            </span>
                          ) : (
                            <button
                              onClick={() => handleMarkAsSent(participant.paricipant_id)}
                              title="Mark as Sent"
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                border: "none",
                                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                color: "#fff",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                justifyContent: "center",
                                fontWeight: 600,
                                fontSize: "11px",
                                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)";
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.3)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)";
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(59, 130, 246, 0.2)";
                              }}
                            >
                              <i className="fa fa-check-circle" style={{ fontSize: "10px" }} />
                              Mark Sent
                            </button>
                          )}
                        </td>
                        <td style={{
                          padding: "12px",
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          minWidth: "120px"
                        }}>
                          <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                            <button
                              onClick={() => handleOpenDetailModal(participant)}
                              title="View Details"
                              style={{
                                padding: "5px 8px",
                                borderRadius: "4px",
                                border: "none",
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "#fff !important",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                minWidth: "45px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                justifyContent: "center",
                                fontWeight: 600,
                                fontSize: "11px",
                                boxShadow: "0 2px 4px rgba(16, 185, 129, 0.2)"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "linear-gradient(135deg, #059669 0%, #047857 100%)";
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = "0 4px 8px rgba(16, 185, 129, 0.3)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "linear-gradient(135deg, #10b981 0%, #059669 100%)";
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(16, 185, 129, 0.2)";
                              }}
                            >
                              <i className="fa fa-eye" style={{ color: "#fff", fontSize: "10px" }} />
                              <span style={{ color: "#fff" }}>View</span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleOpenEditModal(participant);
                              }}
                              title="Edit"
                              style={{
                                padding: "5px 8px",
                                borderRadius: "4px",
                                border: "none",
                                background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                                color: "#fff !important",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                minWidth: "45px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                                justifyContent: "center",
                                fontWeight: 600,
                                fontSize: "11px",
                                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)"
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = `linear-gradient(135deg, ${COLORS.primary_color_shade7 || "#4f46e5"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`;
                                e.currentTarget.style.transform = "translateY(-1px)";
                                e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.3)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`;
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 2px 4px rgba(59, 130, 246, 0.2)";
                              }}
                            >
                              <i className="fa fa-edit" style={{ color: "#fff", fontSize: "10px" }} />
                              <span style={{ color: "#fff" }}>Edit</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
        )}

        {/* Add Participant Buttons - Below Content */}
        <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "24px", paddingTop: "20px", borderTop: `2px solid ${COLORS.grey3 || "#E0E0E0"}` }}>
          <button
            type="button"
            onClick={handleOpenAddSchoolStudentModal}
            style={{
              padding: "12px 32px",
              borderRadius: "12px",
              border: "none",
              background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: "15px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
            }}
          >
            <i className="fa fa-user-graduate" style={{ fontSize: "16px" }} />
            Add from School
          </button>
          <button
            type="button"
            onClick={handleOpenAddOutsideStudentModal}
            style={{
              padding: "12px 32px",
              borderRadius: "12px",
              border: "none",
              background: `linear-gradient(135deg, ${COLORS.green5 || "#4caf50"} 0%, #45a049 100%)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: "15px",
              cursor: "pointer",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(76, 175, 80, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(76, 175, 80, 0.3)";
            }}
          >
            <i className="fa fa-user-plus" style={{ fontSize: "16px" }} />
            Add from Outside
          </button>
        </div>
      </div>


      {/* Registration Details Modal - Now includes student selection */}
      <MdlEventParticipantRegistration
        show={showRegistrationModal}
        onClose={handleCloseRegistrationModal}
        selectedStudents={selectedStudentsForRegistration}
        event_id={event_id}
        session_id={session_id}
        eventPrograms={eventPrograms}
        participantGroups={participantGroups}
        selectedGroup={selectedGroupForAdd}
        onStudentsSelected={handleSelectedStudents}
        onSuccess={() => {
          getParticipantList();
        }}
      />

      {/* Add Participant from Outside Modal - Registration Form */}
      <Modal
        show={showAddOutsideStudentModal}
        onHide={handleCloseAddOutsideStudentModal}
        size="xl"
        centered
        dialogClassName="registration-form-modal"
        style={{ zIndex: 1055 }}
      >
        <Modal.Header
          style={{
            background: `linear-gradient(135deg, ${COLORS.green5 || "#4caf50"} 0%, #45a049 100%)`,
            color: "#fff",
            borderBottom: "none",
          }}
        >
          <Modal.Title style={{ color: "#fff", fontWeight: 700 }}>
            <i className="fa fa-user-plus" style={{ marginRight: "8px" }} />
            Participant Registration Form
          </Modal.Title>
          <button
            type="button"
            className="close"
            onClick={handleCloseAddOutsideStudentModal}
            style={{ color: "#fff", opacity: 1, fontSize: "28px" }}
          >
            <span>&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: 0, height: "80vh", overflow: "hidden" }}>
          <iframe
            src={`${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}Printables/Event/event_participant.php?&event_id=${event_id}&school_code=${SessionManager.getSchoolCode()}`}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              display: "block",
            }}
            title="Participant Registration Form"
            onLoad={() => {
              // Refresh participant list when iframe loads (in case form was submitted)
              setTimeout(() => {
                getParticipantList();
              }, 1000);
            }}
          />
        </Modal.Body>
      </Modal>

      {/* Legacy Add Participant Modal (keeping for backward compatibility) */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
        dialogClassName="participant-modal"
      >
        <Modal.Header
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade6} 0%, ${COLORS.primary_color_shade8} 100%)`,
            color: "#fff",
            borderBottom: "none",
            borderRadius: "10px 10px 0 0",
            overflow: "hidden",
          }}
        >
          <Modal.Title style={{ color: "#fff", fontWeight: 700 }}>
            <i className="fa fa-user-plus" style={{ marginRight: "8px" }} />
            Add New Participant
          </Modal.Title>
          <button
            type="button"
            className="close"
            onClick={handleCloseAddModal}
            style={{ color: "#fff", opacity: 1 }}
          >
            <span>&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    Student Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="student_name"
                    value={formData.student_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    className="form-control"
                    name="student_dob"
                    value={formData.student_dob}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    className="form-control"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Community</label>
                  <input
                    type="text"
                    className="form-control"
                    name="community"
                    value={formData.community}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Religion</label>
                  <input
                    type="text"
                    className="form-control"
                    name="religion"
                    value={formData.religion}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Nationality</label>
                  <input
                    type="text"
                    className="form-control"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Current School Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="current_school_name"
                    value={formData.current_school_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Reading Class</label>
                  <input
                    type="text"
                    className="form-control"
                    name="reading_class"
                    value={formData.reading_class}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    Father Name <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="father_name"
                    value={formData.father_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>Mother Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="mother_name"
                    value={formData.mother_name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    Contact Number <span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    name="contact_number"
                    value={formData.contact_number}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label>WhatsApp Number</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="whatsapp_number"
                    value={formData.whatsapp_number}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Address</label>
              <textarea
                className="form-control"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>Remark</label>
              <textarea
                className="form-control"
                name="remark"
                value={formData.remark}
                onChange={handleInputChange}
                rows="2"
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: `1px solid ${COLORS.grey3}`,
            padding: "12px 24px",
            borderRadius: "0 0 10px 10px",
            overflow: "hidden",
          }}
        >
          <button
            onClick={handleCloseAddModal}
            className="btn btn-secondary"
            style={{ marginRight: "10px" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn btn-primary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin" /> Saving...
              </>
            ) : (
              <>
                <i className="fa fa-save" /> Add Participant
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Edit Participant Modal - Separate Component */}
      <MdlEditEventParticipant
        show={showEditModal}
        onClose={handleCloseEditModal}
        participant={editingParticipant}
        event_id={event_id}
        session_id={session_id}
        participantGroups={participantGroups}
        onSuccess={() => {
          getParticipantList();
        }}
      />

      {/* Payment Entry Modal */}
      <Modal
        show={showPaymentModal}
        onHide={handleClosePaymentModal}
        size="md"
        centered
        backdrop={true}
        keyboard={true}
        dialogClassName="payment-modal"
        animation={false}
      >
        <style>{`
          .payment-modal .modal-content {
            border: none;
            border-radius: 12px;
            overflow: hidden;
            background: transparent;
            box-shadow: 0 18px 40px rgba(15,23,42,0.28);
          }
          .payment-modal .modal-header {
            border-radius: 12px 12px 0 0;
            overflow: hidden;
          }
          .payment-modal .modal-footer {
            border-radius: 0 0 12px 12px;
            overflow: hidden;
          }
        `}</style>
        <Modal.Header
          closeButton={false}
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
            color: "#fff",
            borderBottom: "none",
            borderRadius: "12px 12px 0 0",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
              <i className="fa fa-money" style={{ color: "#fff", fontSize: 16 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Record Payment</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                {selectedParticipant?.student_name || "Payment Entry"}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="close"
            onClick={handleClosePaymentModal}
            style={{ color: "#fff", opacity: 1, fontSize: 24, lineHeight: 1, background: "transparent", border: "none", padding: "4px 8px" }}
          >
            <span>&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: "0", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
          <form id="payment-form" onSubmit={handleSubmitPayment} style={{ padding: "16px" }}>
            <div style={{ 
              background: "linear-gradient(180deg, #f0fdf4 0%, #ffffff 65%)", 
              borderRadius: "10px", 
              padding: "12px 14px", 
              marginBottom: "12px",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05)",
              border: "1px solid #86efac"
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12, fontWeight: 700, color: "#166534", fontSize: "13px" }}>
                <i className="fa fa-credit-card" style={{ fontSize: "14px" }} />
                <span>Payment Details</span>
              </div>
              
              {/* Amount Summary */}
              <div style={{ 
                background: "#fff", 
                borderRadius: "8px", 
                padding: "10px 12px", 
                marginBottom: "12px",
                border: "1px solid #86efac"
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", color: "#166534" }}>
                  <span>Payable Amount:</span>
                  <span style={{ fontWeight: 700 }}>₹{parseFloat(paymentFormData.payable_amount || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", color: "#22c55e" }}>
                  <span>Discount:</span>
                  <span style={{ fontWeight: 700 }}>-₹{parseFloat(paymentFormData.discount_amount || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", color: "#166534" }}>
                  <span>Payment Amount:</span>
                  <span style={{ fontWeight: 700 }}>₹{parseFloat(paymentFormData.payment_amount || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 700, paddingTop: 6, borderTop: "1px solid #e5e7eb", color: parseFloat(calculateDueAmount) > 0 ? "#f59e0b" : "#22c55e" }}>
                  <span>Due Amount:</span>
                  <span>₹{calculateDueAmount}</span>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Payable Amount <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="payable_amount"
                      value={paymentFormData.payable_amount}
                      onChange={handlePaymentInputChange}
                      required
                      min="0"
                      step="0.01"
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Discount Amount
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="discount_amount"
                      value={paymentFormData.discount_amount}
                      onChange={handlePaymentInputChange}
                      min="0"
                      step="0.01"
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Payment Amount <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      name="payment_amount"
                      value={paymentFormData.payment_amount}
                      onChange={handlePaymentInputChange}
                      required
                      min="0.01"
                      step="0.01"
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Payment Date <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="payment_date"
                      value={paymentFormData.payment_date}
                      onChange={handlePaymentInputChange}
                      required
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Payment Mode <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      className="form-control"
                      name="payment_mode"
                      value={paymentFormData.payment_mode}
                      onChange={handlePaymentInputChange}
                      required
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    >
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Card">Card</option>
                      <option value="Online">Online</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group" style={{ marginBottom: "12px" }}>
                    <label style={{ 
                      fontSize: "11px", 
                      fontWeight: 700, 
                      color: "#166534", 
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      marginBottom: "6px",
                      display: "block"
                    }}>
                      Receipt Number
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      name="receipt_no"
                      value={paymentFormData.receipt_no}
                      onChange={handlePaymentInputChange}
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #86efac",
                        padding: "8px 10px",
                        fontSize: "13px",
                        transition: "all 0.2s ease",
                        background: "#fff"
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#22c55e";
                        e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#86efac";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: "12px" }}>
                <label style={{ 
                  fontSize: "11px", 
                  fontWeight: 700, 
                  color: "#166534", 
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                  display: "block"
                }}>
                  Transaction ID / Reference Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  name="transaction_id"
                  value={paymentFormData.transaction_id}
                  onChange={handlePaymentInputChange}
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #86efac",
                    padding: "8px 10px",
                    fontSize: "13px",
                    transition: "all 0.2s ease",
                    background: "#fff"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#22c55e";
                    e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#86efac";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "0" }}>
                <label style={{ 
                  fontSize: "11px", 
                  fontWeight: 700, 
                  color: "#166534", 
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  marginBottom: "6px",
                  display: "block"
                }}>
                  Remark
                </label>
                <textarea
                  className="form-control"
                  name="remark"
                  value={paymentFormData.remark}
                  onChange={handlePaymentInputChange}
                  rows="2"
                  style={{
                    borderRadius: "6px",
                    border: "1px solid #86efac",
                    padding: "8px 10px",
                    fontSize: "13px",
                    transition: "all 0.2s ease",
                    background: "#fff",
                    resize: "vertical"
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#22c55e";
                    e.target.style.boxShadow = "0 0 0 2px rgba(34, 197, 94, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#86efac";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer
          style={{
            borderTop: "1px solid #e5e7eb",
            padding: "12px 16px",
            borderRadius: "0 0 12px 12px",
            overflow: "hidden",
            background: "#f8fafc",
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px"
          }}
        >
          <button
            type="button"
            onClick={handleClosePaymentModal}
            style={{
              padding: "8px 16px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              background: "#fff",
              color: "#374151",
              fontWeight: 600,
              fontSize: "13px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "6px"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#f9fafb";
              e.currentTarget.style.borderColor = "#9ca3af";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#fff";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
          >
            <i className="fa fa-times" style={{ fontSize: "12px" }} />
            Cancel
          </button>
          <button
            type="submit"
            form="payment-form"
            disabled={savingPayment}
            style={{
              padding: "8px 20px",
              borderRadius: "6px",
              border: "none",
              background: savingPayment 
                ? `linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)`
                : `linear-gradient(135deg, #22c55e 0%, #16a34a 100%)`,
              color: "#fff",
              fontWeight: 700,
              fontSize: "13px",
              cursor: savingPayment ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              boxShadow: savingPayment ? "none" : "0 2px 8px rgba(34, 197, 94, 0.3)",
              opacity: savingPayment ? 0.7 : 1
            }}
            onMouseEnter={(e) => {
              if (!savingPayment) {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 12px rgba(34, 197, 94, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!savingPayment) {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(34, 197, 94, 0.3)";
              }
            }}
          >
            {savingPayment ? (
              <>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: "12px" }} />
                Processing...
              </>
            ) : (
              <>
                <i className="fa fa-check" style={{ fontSize: "12px" }} />
                Record Payment
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Participant Detail Modal - Modernized UI */}
      <Modal
        show={showDetailModal}
        onHide={handleCloseDetailModal}
        size="xl"
        centered
        animation={false}
        backdrop={true}
        keyboard={true}
        contentClassName="participant-detail-content"
      >
        <Modal.Header
          closeButton={false}
          style={{
            background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
            color: "#fff",
            borderBottom: "none",
            borderRadius: "12px 12px 0 0",
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
            <div style={{ width: 46, height: 46, borderRadius: "50%", background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
              <i className="fa fa-user" style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ fontWeight: 700, fontSize: 18 }}>Participant</div>
                {participantList && participantList.length > 1 && selectedParticipant && (
                  <span style={{ 
                    fontSize: 12, 
                    opacity: 0.8, 
                    background: "rgba(255,255,255,0.15)", 
                    padding: "2px 8px", 
                    borderRadius: "12px",
                    fontWeight: 600
                  }}>
                    {participantList.findIndex(p => p.paricipant_id === selectedParticipant.paricipant_id) + 1} of {participantList.length}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, opacity: 0.9 }}>
                {selectedParticipant?.student_name || "N/A"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginRight: "12px" }}>
            {participantList && participantList.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePreviousParticipant}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontSize: "14px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                  title="Previous Participant"
                >
                  <i className="fa fa-chevron-left" />
                </button>
                <button
                  type="button"
                  onClick={handleNextParticipant}
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.3)",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    fontSize: "14px"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
                    e.currentTarget.style.transform = "scale(1.1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                  title="Next Participant"
                >
                  <i className="fa fa-chevron-right" />
                </button>
              </>
            )}
          </div>
          <button
            type="button"
            className="close"
            onClick={handleCloseDetailModal}
            style={{ color: "#fff", opacity: 1, fontSize: 26, lineHeight: 1 }}
          >
            <span>&times;</span>
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: "0 0 24px 0", maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", borderRadius: "0 0 12px 12px" }}>
          {selectedParticipant && (
            <div style={{ padding: "20px 24px", display: "grid", gridTemplateColumns: "2fr 1fr", gap: "16px" }}>
              {/* Left content */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ background: `linear-gradient(180deg, ${(COLORS.primary_color_shade1 || "#eef2ff")} 0%, #ffffff 65%)`, borderRadius: "12px", padding: "16px 18px", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)", border: `1px solid ${COLORS.primary_color_shade3 || "#c7d2fe"}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#0f172a" }}>
                      <i className="fa fa-id-card" />
                      Core Details
                    </div>
                    <span style={{ padding: "6px 10px", borderRadius: "999px", background: "#eef2ff", color: "#4338ca", fontSize: 12, fontWeight: 700 }}>
                      ID: {selectedParticipant.paricipant_id || "N/A"}
                    </span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
                    {[
                      { label: "Student Name", value: selectedParticipant.student_name, strong: true },
                      { label: "Father Name", value: selectedParticipant.father_name },
                      { label: "Mother Name", value: selectedParticipant.mother_name },
                      { label: "Gender", value: selectedParticipant.gender },
                      { label: "DOB", value: formatDate(selectedParticipant.student_dob) },
                      { label: "Nationality", value: selectedParticipant.nationality },
                      { label: "Community", value: selectedParticipant.community },
                      { label: "Religion", value: selectedParticipant.religion },
                      { label: "Reading Class", value: selectedParticipant.reading_class },
                      { label: "Current School", value: selectedParticipant.current_school_name },
                      { label: "Address", value: selectedParticipant.address, colSpan: true },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: "#fff",
                          borderRadius: "10px",
                          padding: "10px 12px",
                          gridColumn: item.colSpan ? "1 / -1" : "auto",
                          border: `1px solid ${COLORS.primary_color_shade3 || "#c7d2fe"}`,
                        }}
                      >
                        <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 6, fontWeight: 800 }}>
                          {item.label}
                        </div>
                        <div style={{ fontSize: 13, color: "#0f172a", fontWeight: item.strong ? 750 : 550, lineHeight: 1.5 }}>
                          {item.value || "N/A"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 65%)", borderRadius: "12px", padding: "16px 18px", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)", border: "1px solid #fde68a" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 12 }}>
                    <i className="fa fa-info-circle" />
                    Academic & Program
                    {loadingEventPrograms && (
                      <i className="fa fa-spinner fa-spin" style={{ fontSize: "12px", marginLeft: "8px", opacity: 0.7 }} />
                    )}
                  </div>
                  {loadingEventPrograms ? (
                    <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
                      <i className="fa fa-spinner fa-spin" style={{ fontSize: "20px", marginBottom: "8px" }} />
                      <div style={{ fontSize: "13px" }}>Loading programs...</div>
                    </div>
                  ) : (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px" }}>
                    <div style={{ padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #fde68a" }}>
                      <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 6, fontWeight: 800 }}>
                        Group
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        {participantGroups.find(g => g.group_id === selectedParticipant.group_id)?.group_name || "N/A"}
                      </div>
                    </div>
                    <div style={{ padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #fde68a" }}>
                      <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 6, fontWeight: 800 }}>
                        Program
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                        {eventPrograms.find(p => p.program_id === selectedParticipant.program_id)?.program_name
                          || selectedParticipant.program_name
                          || selectedParticipant.program_id
                          || "N/A"}
                      </div>
                    </div>
                    <div style={{ padding: "10px 12px", borderRadius: "10px", background: "#fff", border: "1px solid #fde68a" }}>
                      <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 6, fontWeight: 800 }}>
                        Contact
                      </div>
                      <div style={{ fontSize: 13, fontFamily: "monospace", color: "#0f172a", marginBottom: 8 }}>
                        {selectedParticipant.contact_number || "N/A"}
                      </div>
                      {(selectedParticipant.whatsapp_number || selectedParticipant.contact_number) && (
                        <button
                          onClick={() => handleWhatsAppMessage(selectedParticipant)}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(37,211,102,0.3)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 6px 16px rgba(37,211,102,0.4)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,211,102,0.3)";
                          }}
                        >
                          <i className="fa fa-whatsapp" style={{ fontSize: 14 }} />
                          Send WhatsApp Message
                        </button>
                      )}
                    </div>
                  </div>
                  )}
                  {selectedParticipant.remark && (
                    <div style={{ marginTop: 12, padding: "12px", borderRadius: "10px", background: "#fff", border: "1px solid #fde68a", fontSize: 13, color: "#0f172a" }}>
                      <div style={{ fontSize: 11, letterSpacing: 0.5, textTransform: "uppercase", color: COLORS.primary_color_shade7 || "#1d4ed8", marginBottom: 4, fontWeight: 800 }}>
                        Remark
                      </div>
                      {selectedParticipant.remark}
                    </div>
                  )}
                </div>

                {/* WhatsApp Message Preview Section */}
                {(selectedParticipant.whatsapp_number || selectedParticipant.contact_number) && (
                  <div style={{ background: "linear-gradient(180deg, #dcfce7 0%, #ffffff 65%)", borderRadius: "12px", padding: "16px 18px", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)", border: "1px solid #86efac" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#15803d" }}>
                        <i className="fa fa-whatsapp" />
                        WhatsApp Message Preview
                        {selectedParticipant.message_send == 1 && (
                          <span style={{
                            marginLeft: "8px",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            background: "rgba(34, 197, 94, 0.2)",
                            color: "#15803d",
                            fontSize: "10px",
                            fontWeight: 600
                          }}>
                            ✓ Sent
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {selectedParticipant.message_send != 1 && (
                          <button
                            onClick={() => handleMarkAsSent(selectedParticipant.paricipant_id)}
                            style={{
                              padding: "8px 16px",
                              borderRadius: "8px",
                              border: "none",
                              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                              color: "#fff",
                              fontWeight: 700,
                              fontSize: 12,
                              cursor: "pointer",
                              boxShadow: "0 4px 12px rgba(59,130,246,0.3)",
                              display: "flex",
                              alignItems: "center",
                              gap: 6,
                              transition: "all 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-1px)";
                              e.currentTarget.style.boxShadow = "0 6px 16px rgba(59,130,246,0.4)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59,130,246,0.3)";
                            }}
                          >
                            <i className="fa fa-check-circle" style={{ fontSize: 12 }} />
                            Mark as Sent
                          </button>
                        )}
                        <button
                          onClick={handleSendWhatsAppMessage}
                          style={{
                            padding: "8px 16px",
                            borderRadius: "8px",
                            border: "none",
                            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 4px 12px rgba(37,211,102,0.3)",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-1px)";
                            e.currentTarget.style.boxShadow = "0 6px 16px rgba(37,211,102,0.4)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(37,211,102,0.3)";
                          }}
                        >
                          <i className="fa fa-paper-plane" style={{ fontSize: 12 }} />
                          Send
                        </button>
                      </div>
                    </div>
                    {(loadingMessagePoints || loadingEventDetails) ? (
                      <div style={{ padding: "20px", textAlign: "center", color: "#64748b" }}>
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: "20px", marginBottom: "8px" }} />
                        <div style={{ fontSize: "13px" }}>Loading message...</div>
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "16px",
                          background: "#fff",
                          borderRadius: "10px",
                          border: "1px solid #86efac",
                          whiteSpace: "pre-wrap",
                          fontFamily: "system-ui, -apple-system, sans-serif",
                          fontSize: "13px",
                          lineHeight: "1.6",
                          color: "#1e293b",
                          maxHeight: "300px",
                          overflowY: "auto",
                        }}
                      >
                        {buildWhatsAppMessage() || "No message content available"}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right sidebar */}
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ background: "#0f172a", color: "#fff", borderRadius: "12px", padding: "16px 18px", boxShadow: "0 12px 30px rgba(15,23,42,0.25)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: "10px", background: "rgba(255,255,255,0.12)", display: "grid", placeItems: "center" }}>
                      <i className="fa fa-credit-card" />
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>Payment</div>
                  </div>
                  
                  {/* Payment Summary */}
                  <div style={{ marginBottom: 12, padding: "10px", background: "rgba(255,255,255,0.08)", borderRadius: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", opacity: 0.9 }}>
                      <span>Payable Amount:</span>
                      <span style={{ fontWeight: 700 }}>₹{parseFloat(selectedParticipant.payable_amount || 0).toFixed(2)}</span>
                    </div>
                    {parseFloat(selectedParticipant.discount_amount || 0) > 0 && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", opacity: 0.9, color: "#22c55e" }}>
                        <span>Discount:</span>
                        <span style={{ fontWeight: 700 }}>-₹{parseFloat(selectedParticipant.discount_amount || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: "12px", opacity: 0.9 }}>
                      <span>Paid Amount:</span>
                      <span style={{ fontWeight: 700 }}>₹{parseFloat(selectedParticipant.paid_amount || 0).toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", fontWeight: 700, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                      <span>Due Amount:</span>
                      <span style={{ color: parseFloat(selectedParticipant.due_amount || 0) > 0 ? "#f59e0b" : "#22c55e" }}>
                        ₹{parseFloat(selectedParticipant.due_amount || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 700,
                      background: selectedParticipant.is_event_fee_paid == 1 ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                      color: "#fff",
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)"
                    }}>
                      {selectedParticipant.is_event_fee_paid == 1 ? "Paid" : "Unpaid"}
                    </span>
                    {selectedParticipant.is_event_fee_paid == 1 ? (
                      <button
                        onClick={() => handleUpdatePaymentStatus(selectedParticipant.paricipant_id, false)}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "linear-gradient(135deg, #f87171 0%, #ef4444 100%)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          boxShadow: "0 8px 18px rgba(0,0,0,0.15)"
                        }}
                      >
                        Mark Unpaid
                      </button>
                    ) : (
                      <button
                        onClick={() => handleOpenPaymentModal(selectedParticipant)}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "none",
                          background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 13,
                          cursor: "pointer",
                          boxShadow: "0 8px 18px rgba(0,0,0,0.15)"
                        }}
                      >
                        <i className="fa fa-money" style={{ marginRight: "6px" }} />
                        Mark Paid
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                    Updated at: {formatDate(selectedParticipant.date_added) || "N/A"}
                  </div>
                </div>

                <div style={{ background: "#fff", borderRadius: "12px", padding: "16px 18px", boxShadow: "0 10px 30px rgba(15, 23, 42, 0.05)", border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>
                    <i className="fa fa-sitemap" />
                    Manage Program
                  </div>
                  <select
                    value={selectedParticipant.program_id || ""}
                    onChange={(e) => handleUpdateProgram(selectedParticipant.paricipant_id, e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      fontSize: "13px",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      background: "#f8fafc",
                      outline: "none",
                      cursor: "pointer"
                    }}
                  >
                    <option value="">Select Program</option>
                    {eventPrograms.map((program) => (
                      <option key={program.program_id} value={program.program_id}>
                        {program.program_name} - {formatDate(program.program_date)} {program.program_time}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", gap: 10 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedParticipant) {
                          handleOpenEditModal(selectedParticipant);
                          handleCloseDetailModal();
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: "12px 14px",
                        borderRadius: "10px",
                        border: "none",
                        background: `linear-gradient(135deg, ${COLORS.primary_color_shade6 || "#3b82f6"} 0%, ${COLORS.primary_color_shade8 || "#1e40af"} 100%)`,
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "13px",
                        cursor: "pointer",
                        boxShadow: "0 10px 20px rgba(59,130,246,0.25)"
                      }}
                    >
                      <i className="fa fa-edit" style={{ marginRight: 8 }} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteParticipant(selectedParticipant.paricipant_id)}
                      style={{
                        flex: 1,
                        padding: "12px 14px",
                        borderRadius: "10px",
                        border: "none",
                        background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "13px",
                        cursor: "pointer",
                        boxShadow: "0 10px 20px rgba(239,68,68,0.25)"
                      }}
                    >
                      <i className="fa fa-trash" style={{ marginRight: 8 }} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

    </div>
  );
};

export default ManageEventParticipant;
